/**
 * LOCKED STOREFRONT INVARIANT — Muskle product media ↔ flavour/variant sync
 * -------------------------------------------------------------------------
 * Bidirectional synchronization between the product gallery and Shopify
 * flavour (SMAAK) / size options is a permanent storefront requirement.
 *
 * DO NOT remove, bypass, or duplicate this controller with ad-hoc listeners.
 * DO NOT match flavours from image filenames or translated labels when
 * Shopify media IDs / featured_media / exact media.alt option values exist.
 *
 * Rules:
 *  A) Flavour/size change → resolve full variant → update id, URL, price,
 *     stock, ATC, points, gallery featured media, active thumb.
 *  B) Thumbnail / media click → resolve media_id (and optional exact alt)
 *     → select matching flavour, preserve size when possible → update all.
 *  C) ?variant= on load selects that variant and its media.
 *  D) Guarding flags prevent event loops / duplicate URL writes.
 *
 * Loaded once per product hero; initialize via MuskleVariantMediaSync.init(root).
 */
(function (global) {
  'use strict';

  var CONTROLLER_ATTR = 'data-muskle-variant-media-sync';

  function parseJson(el) {
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (err) {
      console.error('[MuskleVariantMediaSync] JSON parse failed', el.id, err);
      return null;
    }
  }

  function escapeHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  /** Match Liquid muskle-styled-muskle-text for dynamic points line updates. */
  function styleMuskleBrandText(str) {
    var raw = str == null ? '' : String(str);
    if (raw.indexOf('MUSKLE') === -1) return escapeHtml(raw);
    var parts = raw.split('MUSKLE');
    var html = '';
    for (var i = 0; i < parts.length; i++) {
      if (i > 0) {
        html +=
          '<span class="muskle-wordmark-text" aria-label="MUSKLE">MUS<span class="muskle-red-k">K</span>LE</span>';
      }
      html += escapeHtml(parts[i]);
    }
    return html;
  }

  function opts(v) {
    return [v.option1, v.option2, v.option3];
  }

  function createController(ctx) {
    var sid = ctx.sid;
    var meta = ctx.meta;
    var variants = ctx.variants || [];
    var prices = ctx.prices || {};
    var variantGallery = ctx.variantGallery || {};
    var mediaIndex = ctx.mediaIndex || {};
    var i18n = ctx.i18n || {};
    var pointsMap = ctx.pointsMap;
    var form = ctx.form;
    var heroRoot = ctx.heroRoot;
    var galleryMain = ctx.galleryMain;
    var galleryWrap = ctx.galleryWrap;
    var flavor = meta.selFlavor || '';
    var size = meta.selSize || '';
    var root = form.querySelector('[data-muskle-variant-ui="' + sid + '"]');
    var flavorSelectEl = root ? root.querySelector('[data-muskle-flavor-select]') : null;
    var fallbackSelect = form.querySelector('[data-muskle-fallback-variant]');
    var STOCK_AVAILABLE_MSG = i18n.stockAvailable || '';
    var STOCK_UNAVAILABLE_MSG = i18n.soldOut || '';
    var syncingFromMedia = false;
    var syncingFromVariant = false;

    function findVariant(flavorValue, sizeValue) {
      var fi = meta.flavorIdx;
      var si = meta.sizeIdx;
      for (var i = 0; i < variants.length; i++) {
        var v = variants[i];
        var o = opts(v);
        if (fi >= 0 && o[fi] !== flavorValue) continue;
        if (si >= 0 && o[si] !== sizeValue) continue;
        return v;
      }
      return null;
    }

    function findFlavorSiblingWithMedia(flavorValue, preferredSize) {
      var fi = meta.flavorIdx;
      if (fi < 0 || !flavorValue) return null;
      var preferred = null;
      var available = null;
      var any = null;
      for (var i = 0; i < variants.length; i++) {
        var v = variants[i];
        var o = opts(v);
        if (o[fi] !== flavorValue) continue;
        var spec = variantGallery[String(v.id)];
        if (!spec || spec.media_id == null) continue;
        any = any || v;
        if (v.available) available = available || v;
        if (preferredSize != null && meta.sizeIdx >= 0 && o[meta.sizeIdx] === preferredSize) {
          preferred = v;
          break;
        }
      }
      return preferred || available || any;
    }

    function refreshSizeCardPrices(currentFlavor) {
      var rootUi = form.querySelector('[data-muskle-variant-ui="' + sid + '"]');
      if (!rootUi) return;
      rootUi.querySelectorAll('[data-muskle-pill-type="size"]').forEach(function (sb) {
        var sv = sb.getAttribute('data-muskle-pill-value');
        var vv = findVariant(currentFlavor, sv);
        var pe = sb.querySelector('.muskle-product-hero__size-card-price');
        if (!pe) return;
        if (vv && prices[String(vv.id)]) pe.textContent = prices[String(vv.id)].price_money;
        else pe.textContent = '—';
      });
    }

    function scrollActiveThumbIntoView(activeThumb) {
      if (!activeThumb || !galleryWrap) return;
      var thumbs = galleryWrap.querySelector('.muskle-product-hero__gallery-thumbs');
      if (!thumbs || typeof activeThumb.scrollIntoView !== 'function') return;
      var reduceMotion =
        window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      try {
        activeThumb.scrollIntoView({
          behavior: reduceMotion ? 'auto' : 'smooth',
          inline: 'nearest',
          block: 'nearest',
        });
      } catch (err) {
        activeThumb.scrollIntoView(false);
      }
    }

    function readCurrentOptionValues() {
      var currentFlavor = flavor;
      var currentSize = size;
      if (flavorSelectEl) currentFlavor = flavorSelectEl.value || currentFlavor;
      if (root) {
        var activeFlavor = root.querySelector('[data-muskle-pill-type="flavor"].is-active');
        if (activeFlavor) {
          currentFlavor = activeFlavor.getAttribute('data-muskle-pill-value') || currentFlavor;
        }
        var activeSize = root.querySelector('[data-muskle-pill-type="size"].is-active');
        if (activeSize) {
          currentSize = activeSize.getAttribute('data-muskle-pill-value') || currentSize;
        }
      }
      return { flavor: currentFlavor, size: currentSize };
    }

    function collectMatchesForMedia(mediaId, mediaAlt) {
      var mid = mediaId != null && mediaId !== '' ? String(mediaId) : '';
      var matches = [];
      var seen = {};

      function pushUnique(v) {
        if (!v || seen[v.id]) return;
        seen[v.id] = true;
        matches.push(v);
      }

      if (mid && mediaIndex.byMediaId && mediaIndex.byMediaId[mid]) {
        var ids = mediaIndex.byMediaId[mid];
        for (var a = 0; a < ids.length; a++) {
          for (var i = 0; i < variants.length; i++) {
            if (String(variants[i].id) === String(ids[a])) pushUnique(variants[i]);
          }
        }
      }

      if (mid) {
        for (var j = 0; j < variants.length; j++) {
          var v = variants[j];
          var spec = variantGallery[String(v.id)];
          if (spec && spec.media_id != null && String(spec.media_id) === mid) pushUnique(v);
        }
      }

      // Structured mapping only: media.alt exactly equals a flavour option value.
      if (mediaAlt && meta.flavorIdx >= 0 && mediaIndex.altToFlavor) {
        var altKey = String(mediaAlt).trim().toLowerCase();
        var flavorFromAlt = mediaIndex.altToFlavor[altKey];
        if (flavorFromAlt) {
          for (var k = 0; k < variants.length; k++) {
            var ov = opts(variants[k]);
            if (ov[meta.flavorIdx] === flavorFromAlt) pushUnique(variants[k]);
          }
        }
      }

      return matches;
    }

    function scoreVariantMatch(candidate, current) {
      var o = opts(candidate);
      var score = 0;
      if (candidate.available) score += 20;
      if (meta.sizeIdx >= 0 && current.size && o[meta.sizeIdx] === current.size) score += 100;
      if (meta.flavorIdx >= 0 && current.flavor && o[meta.flavorIdx] === current.flavor) score += 40;
      return score;
    }

    function findBestVariantForMedia(mediaId, mediaAlt) {
      var matches = collectMatchesForMedia(mediaId, mediaAlt);
      if (!matches.length) return null;
      if (matches.length === 1) return matches[0];
      var current = readCurrentOptionValues();
      var best = null;
      var bestScore = -1;
      for (var m = 0; m < matches.length; m++) {
        var score = scoreVariantMatch(matches[m], current);
        if (score > bestScore) {
          bestScore = score;
          best = matches[m];
        }
      }
      return best || matches[0];
    }

    function syncOptionControlsFromVariant(variant) {
      if (!variant) return;
      var o = opts(variant);
      if (meta.flavorIdx >= 0 && o[meta.flavorIdx] != null) {
        flavor = o[meta.flavorIdx];
        if (flavorSelectEl) flavorSelectEl.value = flavor;
        if (root) {
          root.querySelectorAll('[data-muskle-pill-type="flavor"]').forEach(function (b) {
            var on = b.getAttribute('data-muskle-pill-value') === flavor;
            b.classList.toggle('is-active', on);
            b.setAttribute('aria-checked', on ? 'true' : 'false');
          });
        }
      }
      if (meta.sizeIdx >= 0 && o[meta.sizeIdx] != null) {
        size = o[meta.sizeIdx];
        if (root) {
          root.querySelectorAll('[data-muskle-pill-type="size"]').forEach(function (b) {
            var on = b.getAttribute('data-muskle-pill-value') === size;
            b.classList.toggle('is-active', on);
            b.setAttribute('aria-checked', on ? 'true' : 'false');
          });
        }
      }
      if (fallbackSelect) fallbackSelect.value = String(variant.id);
      if (meta.premium && flavor) refreshSizeCardPrices(flavor);
    }

    function updateVariantUrl(variant) {
      try {
        if (!variant || !window.history || !window.history.replaceState) return;
        var url = new URL(window.location.href);
        var next = String(variant.id);
        if (url.searchParams.get('variant') === next) return;
        url.searchParams.set('variant', next);
        window.history.replaceState({}, '', url.toString());
      } catch (err) {}
    }

    function resolveGallerySpec(variant) {
      if (!variant) return null;
      var spec = variantGallery[String(variant.id)];
      if (spec && spec.src) return spec;
      if (meta.flavorIdx < 0) return null;
      var flavorValue = opts(variant)[meta.flavorIdx];
      var preferredSize = meta.sizeIdx >= 0 ? opts(variant)[meta.sizeIdx] : null;
      var sibling = findFlavorSiblingWithMedia(flavorValue, preferredSize);
      if (!sibling) return null;
      return variantGallery[String(sibling.id)] || null;
    }

    function applyGalleryForVariant(variant) {
      if (!galleryMain || !galleryWrap) return;
      var spec = resolveGallerySpec(variant);
      var gsrc = null;
      var gsrcset = null;
      var gw = null;
      var gh = null;
      var gmediaId = null;
      if (spec && spec.src) {
        gsrc = spec.src;
        gsrcset = spec.srcset;
        gw = spec.w;
        gh = spec.h;
        gmediaId = spec.media_id;
      } else {
        gsrc = galleryWrap.getAttribute('data-muskle-fallback-src');
        gsrcset = galleryWrap.getAttribute('data-muskle-fallback-srcset');
        gw = galleryWrap.getAttribute('data-muskle-fallback-w');
        gh = galleryWrap.getAttribute('data-muskle-fallback-h');
        gmediaId = galleryWrap.getAttribute('data-muskle-fallback-media-id');
      }
      if (!gsrc) return;
      galleryMain.src = gsrc;
      if (gsrcset) galleryMain.setAttribute('srcset', gsrcset);
      else galleryMain.removeAttribute('srcset');
      if (gw) galleryMain.setAttribute('width', gw);
      else galleryMain.removeAttribute('width');
      if (gh) galleryMain.setAttribute('height', gh);
      else galleryMain.removeAttribute('height');
      var activeThumbEl = null;
      galleryWrap.querySelectorAll('.muskle-product-hero__thumb').forEach(function (b) {
        var mid = b.getAttribute('data-media-id');
        var on = gmediaId != null && gmediaId !== '' && mid === String(gmediaId);
        b.classList.toggle('is-active', on);
        b.setAttribute('aria-selected', on ? 'true' : 'false');
        if (on) activeThumbEl = b;
      });
      if (activeThumbEl) scrollActiveThumbIntoView(activeThumbEl);
    }

    function showThumbMedia(btn) {
      if (!galleryMain || !btn) return;
      var tsrc = btn.getAttribute('data-muskle-src');
      var tsrcset = btn.getAttribute('data-muskle-srcset');
      var tw = btn.getAttribute('data-muskle-w');
      var th = btn.getAttribute('data-muskle-h');
      if (tsrc) galleryMain.src = tsrc;
      if (tsrcset) galleryMain.setAttribute('srcset', tsrcset);
      else galleryMain.removeAttribute('srcset');
      if (tw) galleryMain.setAttribute('width', tw);
      else galleryMain.removeAttribute('width');
      if (th) galleryMain.setAttribute('height', th);
      else galleryMain.removeAttribute('height');
      galleryWrap.querySelectorAll('.muskle-product-hero__thumb').forEach(function (b) {
        b.classList.remove('is-active');
        b.setAttribute('aria-selected', 'false');
      });
      btn.classList.add('is-active');
      btn.setAttribute('aria-selected', 'true');
      scrollActiveThumbIntoView(btn);
    }

    function updateUi(variant, options) {
      if (!variant) return;
      options = options || {};
      var fromMedia = options.fromMedia === true;
      var idInput = form.querySelector('[data-muskle-variant-id]');
      if (idInput) idInput.value = variant.id;
      var idSelect = form.querySelector('select[name="id"]');
      if (idSelect) idSelect.value = String(variant.id);
      var p = prices[String(variant.id)];
      var priceEl = form.querySelector('[data-muskle-price-current]');
      var compareEl = form.querySelector('[data-muskle-price-compare]');
      if (priceEl && p) priceEl.textContent = p.price_money;
      if (compareEl) {
        if (p && p.compare_money) {
          compareEl.textContent = p.compare_money;
          compareEl.hidden = false;
        } else {
          compareEl.textContent = '';
          compareEl.hidden = true;
        }
      }
      var pointsLine = form.querySelector('[data-muskle-points-line]');
      var pointsText = form.querySelector('[data-muskle-points-text]');
      if (pointsLine && pointsMap && pointsMap.variants) {
        var ptsRow = pointsMap.variants[String(variant.id)] || null;
        var resolvedPts = ptsRow && ptsRow.resolved != null ? ptsRow.resolved : pointsMap.productFallback;
        if (resolvedPts != null && resolvedPts !== '' && Number(resolvedPts) > 0) {
          var prefix = pointsMap.prefix || 'Verdien ';
          var suffix = pointsMap.suffix || ' MUSKLE punten met dit product';
          if (pointsText) pointsText.innerHTML = styleMuskleBrandText(prefix + String(resolvedPts) + suffix);
          pointsLine.hidden = false;
        } else {
          if (pointsText) pointsText.innerHTML = '';
          pointsLine.hidden = true;
        }
      }
      var avail = variant.available === true;
      var btn = form.querySelector('[data-muskle-atc-btn]');
      if (btn) {
        var btnLabel = btn.querySelector('[data-muskle-atc-label]');
        var btnIcon = btn.querySelector('[data-muskle-atc-icon]');
        if (avail) {
          btn.disabled = false;
          if (btnLabel) btnLabel.textContent = i18n.addToCart || '';
          if (btnIcon) btnIcon.hidden = false;
        } else {
          btn.disabled = true;
          if (btnLabel) btnLabel.textContent = i18n.soldOut || '';
          if (btnIcon) btnIcon.hidden = true;
        }
      }
      var stockDot = heroRoot ? heroRoot.querySelector('[data-muskle-stock-dot]') : null;
      var stockText = heroRoot ? heroRoot.querySelector('[data-muskle-stock-text]') : null;
      if (stockDot && stockText) {
        stockDot.classList.add('muskle-product-hero__stock-dot');
        stockDot.classList.toggle('muskle-product-hero__stock-dot--in-stock', avail);
        stockDot.classList.toggle('muskle-product-hero__stock-dot--available', avail);
        stockDot.classList.toggle('muskle-product-hero__stock-dot--unavailable', !avail);
        stockDot.style.setProperty('background', avail ? '#16a34a' : 'rgba(142, 142, 147, 0.95)', 'important');
        stockDot.style.setProperty(
          'background-color',
          avail ? '#16a34a' : 'rgba(142, 142, 147, 0.95)',
          'important'
        );
        stockDot.style.setProperty(
          'box-shadow',
          avail ? '0 0 0 1px rgba(22, 163, 74, 0.35)' : '0 0 0 1px rgba(255, 59, 48, 0.35)',
          'important'
        );
        stockText.textContent = avail ? STOCK_AVAILABLE_MSG : STOCK_UNAVAILABLE_MSG;
      }
      if (heroRoot) {
        heroRoot.querySelectorAll('[data-muskle-member-actions]').forEach(function (actions) {
          actions.setAttribute('data-variant-id', String(variant.id || ''));
          actions.setAttribute('data-variant-available', avail ? 'true' : 'false');
        });
        heroRoot.dispatchEvent(
          new CustomEvent('muskle:variant-availability', {
            bubbles: true,
            detail: { available: avail, variantId: variant.id, root: heroRoot },
          })
        );
      }
      if (!fromMedia) applyGalleryForVariant(variant);
      updateVariantUrl(variant);
    }

    function getInitialVariant() {
      var idInput = form.querySelector('[data-muskle-variant-id]');
      if (!idInput) return variants[0] || null;
      var vid = String(idInput.value);
      for (var k = 0; k < variants.length; k++) {
        if (String(variants[k].id) === vid) return variants[k];
      }
      return variants[0] || null;
    }

    function applyVariantFromControls() {
      if (syncingFromMedia) return;
      syncingFromVariant = true;
      try {
        var v = findVariant(flavor, size);
        if (!v && meta.flavorIdx >= 0) {
          // Prefer any available size for the selected flavour.
          for (var i = 0; i < variants.length; i++) {
            var cand = variants[i];
            if (opts(cand)[meta.flavorIdx] !== flavor) continue;
            if (!v || (cand.available && !v.available)) v = cand;
          }
        }
        if (v) {
          if (meta.sizeIdx >= 0) size = opts(v)[meta.sizeIdx] || size;
          refreshSizeCardPrices(flavor);
          updateUi(v, { fromMedia: false });
        }
      } finally {
        syncingFromVariant = false;
      }
    }

    function onPopState() {
      try {
        var vid = new URL(window.location.href).searchParams.get('variant');
        if (!vid) return;
        for (var i = 0; i < variants.length; i++) {
          if (String(variants[i].id) === String(vid)) {
            syncingFromMedia = true;
            try {
              syncOptionControlsFromVariant(variants[i]);
              updateUi(variants[i], { fromMedia: false });
            } finally {
              syncingFromMedia = false;
            }
            return;
          }
        }
      } catch (err) {}
    }

    if (meta.premium && root) {
      if (flavorSelectEl) {
        flavor = flavorSelectEl.value || flavor;
        flavorSelectEl.addEventListener('change', function () {
          if (syncingFromMedia) return;
          flavor = flavorSelectEl.value || '';
          applyVariantFromControls();
        });
      }
      root.querySelectorAll('[data-muskle-pill-type]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          if (syncingFromMedia) return;
          var t = btn.getAttribute('data-muskle-pill-type');
          var val = btn.getAttribute('data-muskle-pill-value');
          if (t === 'flavor') flavor = val;
          if (t === 'size') size = val;
          root.querySelectorAll('[data-muskle-pill-type="' + t + '"]').forEach(function (b) {
            b.classList.toggle('is-active', b === btn);
            b.setAttribute('aria-checked', b === btn ? 'true' : 'false');
          });
          applyVariantFromControls();
        });
      });
      refreshSizeCardPrices(flavor);
    }

    if (fallbackSelect) {
      fallbackSelect.addEventListener('change', function () {
        if (syncingFromMedia) return;
        var id = fallbackSelect.value;
        var v = null;
        for (var j = 0; j < variants.length; j++) {
          if (String(variants[j].id) === String(id)) {
            v = variants[j];
            break;
          }
        }
        if (v) {
          syncingFromVariant = true;
          try {
            syncOptionControlsFromVariant(v);
            updateUi(v, { fromMedia: false });
          } finally {
            syncingFromVariant = false;
          }
        }
      });
    }

    var initVariant = getInitialVariant();
    if (initVariant) {
      syncOptionControlsFromVariant(initVariant);
      updateUi(initVariant, { fromMedia: false });
    }

    if (galleryWrap && galleryMain) {
      galleryWrap.querySelectorAll('.muskle-product-hero__thumb').forEach(function (btn) {
        btn.addEventListener('click', function () {
          if (syncingFromVariant) return;
          if (btn.classList.contains('is-active')) return;

          var mediaId = btn.getAttribute('data-media-id');
          var mediaAlt = btn.getAttribute('data-muskle-media-alt') || '';
          var matched = findBestVariantForMedia(mediaId, mediaAlt);

          showThumbMedia(btn);

          if (!matched) {
            // Generic / lifestyle media: show image only; keep selected variant.
            return;
          }

          syncingFromMedia = true;
          try {
            syncOptionControlsFromVariant(matched);
            updateUi(matched, { fromMedia: true });
          } finally {
            syncingFromMedia = false;
          }
        });
      });

      var initialActive = galleryWrap.querySelector('.muskle-product-hero__thumb.is-active');
      if (initialActive) scrollActiveThumbIntoView(initialActive);
    }

    window.addEventListener('popstate', onPopState);

    return {
      applyVariantFromControls: applyVariantFromControls,
      findBestVariantForMedia: findBestVariantForMedia,
      updateUi: updateUi,
    };
  }

  function initFromHero(heroRoot) {
    if (!heroRoot || heroRoot.getAttribute(CONTROLLER_ATTR) === '1') return null;
    var sid = heroRoot.id && heroRoot.id.indexOf('MuskleProductHero-') === 0
      ? heroRoot.id.slice('MuskleProductHero-'.length)
      : heroRoot.getAttribute('data-muskle-gallery-root');
    if (!sid) return null;

    var metaEl = document.getElementById('MuskleHeroVariantMeta-' + sid);
    var variantsEl = document.getElementById('MuskleHeroVariants-' + sid);
    var pricesEl = document.getElementById('MuskleHeroVariantPrices-' + sid);
    var variantGalleryEl = document.getElementById('MuskleHeroVariantGallery-' + sid);
    var mediaIndexEl = document.getElementById('MuskleHeroMediaIndex-' + sid);
    var i18nEl = document.getElementById('MuskleHeroI18n-' + sid);
    var pointsEl = document.getElementById('MuskleHeroVariantPoints-' + sid);
    if (!metaEl || !variantsEl || !pricesEl) return null;

    var form = document.getElementById('MuskleHeroForm-' + sid);
    if (!form) return null;

    var pointsMap = null;
    try {
      pointsMap = pointsEl ? JSON.parse(pointsEl.textContent) : null;
    } catch (errPoints) {
      pointsMap = null;
    }

    heroRoot.setAttribute(CONTROLLER_ATTR, '1');
    return createController({
      sid: sid,
      meta: parseJson(metaEl) || {},
      variants: parseJson(variantsEl) || [],
      prices: parseJson(pricesEl) || {},
      variantGallery: parseJson(variantGalleryEl) || {},
      mediaIndex: parseJson(mediaIndexEl) || {},
      i18n: parseJson(i18nEl) || {},
      pointsMap: pointsMap,
      form: form,
      heroRoot: heroRoot,
      galleryMain: heroRoot.querySelector('#MuskleHeroGalleryMain-' + sid),
      galleryWrap: heroRoot.querySelector('[data-muskle-gallery]'),
    });
  }

  function boot() {
    document.querySelectorAll('[id^="MuskleProductHero-"]').forEach(function (root) {
      initFromHero(root);
    });
  }

  global.MuskleVariantMediaSync = {
    init: initFromHero,
    boot: boot,
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})(typeof window !== 'undefined' ? window : this);
